<template>
  <div class="content target-recruit-page">
    <div class="breadcrumb">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/overview' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>用户管理</el-breadcrumb-item>
        <el-breadcrumb-item>报录比分析</el-breadcrumb-item>
      </el-breadcrumb>
      <el-button size="small" icon="el-icon-refresh" :loading="statisticLoading || listLoading" @click="refresh">刷新</el-button>
    </div>

    <el-card class="overview-card" shadow="never">
      <div v-loading="statisticLoading" class="overview-statistic">
        <span>设置目标用户总数</span>
        <strong>{{ formatInteger(statistic.total_target_count) }}</strong>
        <small>服务端每 10 分钟更新</small>
      </div>
      <div class="scope-statistic">
        <span>当前对比口径</span>
        <strong>{{ scopeText }}</strong>
        <small>录取年份由接口数据统一决定，不参与筛选</small>
      </div>
    </el-card>

    <el-card class="filter-card" shadow="never">
      <el-form :inline="true" size="small" class="filter-form" @submit.native.prevent>
        <el-form-item label="查看维度">
          <el-radio-group v-model="viewMode" @change="handleModeChange">
            <el-radio-button label="major">按专业看院校</el-radio-button>
            <el-radio-button label="college">按院校看专业</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="keywordLabel">
          <el-input
            v-model.trim="query.keyword"
            class="keyword-input"
            clearable
            prefix-icon="el-icon-search"
            :placeholder="keywordPlaceholder"
            @keyup.enter.native="getRankings"
          />
        </el-form-item>
        <el-form-item label="目标年份">
          <el-select v-model="query.target_year" clearable placeholder="全部年份">
            <el-option v-for="year in targetYears" :key="year" :label="year + '年'" :value="year" />
          </el-select>
        </el-form-item>
        <el-form-item label="学习方式">
          <el-select v-model="query.enrol_type" clearable placeholder="全部方式">
            <el-option v-for="item in enrolTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="录取年份">
          <el-input class="recruit-year-input" :value="systemRecruitYear + '年（系统口径）'" disabled />
        </el-form-item>
        <el-form-item class="filter-actions">
          <el-button @click="resetFilters">重置</el-button>
          <el-button type="primary" :loading="listLoading" @click="getRankings">查询</el-button>
        </el-form-item>
      </el-form>
      <div class="filter-tip">
        <i class="el-icon-info" />
        {{ query.keyword ? `当前按“${query.keyword}”筛选` : '未填写关键字时展示当前年份与学习方式下的全局排行' }}
      </div>
    </el-card>

    <div class="ranking-summary">
      <span>匹配有效数据 <strong>{{ matchingCount }}</strong> 条</span>
      <span v-if="lastUpdatedAt">榜单更新于 {{ lastUpdatedAt }}</span>
    </div>

    <div v-loading="listLoading" class="ranking-grid">
      <section class="ranking-panel hot-panel">
        <header class="ranking-header">
          <div>
            <h2><i class="el-icon-top" />热门榜 TOP 10</h2>
            <p>{{ rankingDescription }}，按实时意向报录比从高到低</p>
          </div>
          <span class="ranking-count">{{ hotList.length }} 条</span>
        </header>
        <div class="ranking-column-head"><span>排名 / 院校专业</span><span>实时意向报录比</span></div>
        <ol v-if="hotList.length" class="ranking-list">
          <li v-for="(row, index) in hotList" :key="rowKey(row, index, 'hot')">
            <span :class="['rank-number', { top: index < 3 }]">{{ rankLabel(index) }}</span>
            <div class="target-info">
              <strong :title="entityTitle(row)">{{ entityTitle(row) }}</strong>
              <small :title="entityMeta(row)">{{ entityMeta(row) }}</small>
              <p>当前意向 <b>{{ numberValue(row.target_count_live) }}</b> 人<span />一志愿录取 <b>{{ numberValue(row.recruit_count_pre) }}</b> 人</p>
            </div>
            <div class="rate-value">{{ formatRate(row.target_recruit_rate) }} <small>: 1</small></div>
          </li>
        </ol>
        <div v-else class="ranking-empty"><i class="el-icon-data-analysis" /><p>暂无有效热门榜数据</p></div>
        <footer>热门仅代表研盒当前意向样本较多，不代表实际竞争更激烈。</footer>
      </section>

      <section class="ranking-panel cold-panel">
        <header class="ranking-header">
          <div>
            <h2><i class="el-icon-bottom" />冷门榜 TOP 10</h2>
            <p>{{ rankingDescription }}，按实时意向报录比从低到高</p>
          </div>
          <span class="ranking-count">{{ coldList.length }} 条</span>
        </header>
        <div class="ranking-column-head"><span>排名 / 院校专业</span><span>实时意向报录比</span></div>
        <ol v-if="coldList.length" class="ranking-list">
          <li v-for="(row, index) in coldList" :key="rowKey(row, index, 'cold')">
            <span :class="['rank-number', { top: index < 3 }]">{{ rankLabel(index) }}</span>
            <div class="target-info">
              <strong :title="entityTitle(row)">{{ entityTitle(row) }}</strong>
              <small :title="entityMeta(row)">{{ entityMeta(row) }}</small>
              <p>当前意向 <b>{{ numberValue(row.target_count_live) }}</b> 人<span />一志愿录取 <b>{{ numberValue(row.recruit_count_pre) }}</b> 人</p>
            </div>
            <div class="rate-value">{{ formatRate(row.target_recruit_rate) }} <small>: 1</small></div>
          </li>
        </ol>
        <div v-else class="ranking-empty"><i class="el-icon-data-analysis" /><p>暂无有效冷门榜数据</p></div>
        <footer>冷门仅代表研盒当前意向样本较少，不代表更容易录取。</footer>
      </section>
    </div>

    <el-alert
      v-if="overlapCount"
      class="overlap-alert"
      type="info"
      :closable="false"
      show-icon
      :title="`当前有效样本较少，热门榜与冷门榜有 ${overlapCount} 条数据重合。`"
    />
  </div>
</template>

<script>
import { targetRecruitStatisticComb, targetRecruitTargetList } from '@/request/api'

const DEFAULT_QUERY = () => ({ keyword: '', target_year: '2027', enrol_type: 1 })

export default {
  name: 'TargetRecruitAnalysis',
  data() {
    return {
      statistic: {},
      hotList: [],
      coldList: [],
      hotCount: 0,
      coldCount: 0,
      viewMode: 'major',
      query: DEFAULT_QUERY(),
      targetYears: ['2026', '2027', '2028', '2029', '2030'],
      enrolTypeOptions: [
        { label: '全日制', value: 1 },
        { label: '非全日制', value: 2 },
      ],
      recruitYears: [],
      statisticLoading: false,
      listLoading: false,
      rankingRequestId: 0,
      statisticTimer: null,
    }
  },
  computed: {
    keywordLabel() { return this.viewMode === 'major' ? '专业关键字' : '院校关键字' },
    keywordPlaceholder() { return this.viewMode === 'major' ? '输入专业代码或名称' : '输入院校代码或名称' },
    systemRecruitYear() { return this.recruitYears.length ? this.recruitYears.join(' / ') : '--' },
    enrolTypeLabel() {
      const selected = this.enrolTypeOptions.find((item) => item.value === this.query.enrol_type)
      return selected ? selected.label : '全部学习方式'
    },
    scopeText() {
      return `${this.query.target_year || '全部年份'}目标 · ${this.systemRecruitYear}录取 · ${this.enrolTypeLabel}`
    },
    matchingCount() { return Math.max(this.hotCount, this.coldCount) },
    rankingDescription() { return this.viewMode === 'major' ? '同专业院校' : '同院校专业' },
    lastUpdatedAt() {
      const timestamps = this.hotList.concat(this.coldList).map((row) => Number(row.update_time)).filter((value) => value > 0)
      if (!timestamps.length) return ''
      return this.$moment(Math.max.apply(null, timestamps)).format('YYYY-MM-DD HH:mm')
    },
    overlapCount() {
      if (!this.hotList.length || !this.coldList.length) return 0
      const hotKeys = this.hotList.map((row) => this.recordKey(row))
      return this.coldList.filter((row) => hotKeys.indexOf(this.recordKey(row)) > -1).length
    },
  },
  created() {
    this.refresh()
    this.statisticTimer = setInterval(this.getStatistic, 10 * 60 * 1000)
  },
  beforeDestroy() {
    clearInterval(this.statisticTimer)
    this.statisticTimer = null
  },
  methods: {
    async refresh() { await Promise.all([this.getStatistic(), this.getRankings()]) },
    async getStatistic() {
      this.statisticLoading = true
      try {
        const res = await targetRecruitStatisticComb()
        if (res && res.code === 0) this.statistic = res.data || {}
      } finally {
        this.statisticLoading = false
      }
    },
    async getRankings() {
      const requestId = ++this.rankingRequestId
      const params = this.listParams()
      this.listLoading = true
      try {
        const responses = await Promise.all([
          targetRecruitTargetList(Object.assign({}, params, { order_type: 1 })).catch(() => null),
          targetRecruitTargetList(Object.assign({}, params, { order_type: 2 })).catch(() => null),
        ])
        if (requestId !== this.rankingRequestId) return
        const hotResponse = responses[0]
        const coldResponse = responses[1]
        this.hotList = this.extractValidList(hotResponse)
        this.coldList = this.extractValidList(coldResponse)
        this.hotCount = this.extractCount(hotResponse, this.hotList.length)
        this.coldCount = this.extractCount(coldResponse, this.coldList.length)
        this.updateRecruitYears()
      } finally {
        if (requestId === this.rankingRequestId) this.listLoading = false
      }
    },
    listParams() {
      const params = { pageIndex: 1, pageSize: 10 }
      if (this.query.target_year) params.target_year = this.query.target_year
      if (this.query.enrol_type !== '' && this.query.enrol_type !== undefined && this.query.enrol_type !== null) params.enrol_type = this.query.enrol_type
      if (this.query.keyword) params[this.viewMode === 'major' ? 'major_keyword' : 'college_keyword'] = this.query.keyword
      return params
    },
    extractValidList(res) {
      const list = res && res.code === 0 && res.data && Array.isArray(res.data.list) ? res.data.list : []
      return list.filter((row) => this.isValidRankingRow(row)).slice(0, 10)
    },
    extractCount(res, fallback) {
      const count = res && res.code === 0 && res.data ? Number(res.data.count) : NaN
      return Number.isFinite(count) ? count : fallback
    },
    isValidRankingRow(row) {
      return Number(row.target_count_live) > 0 && Number(row.recruit_count_pre) > 0 && Number(row.target_recruit_rate) >= 0
    },
    updateRecruitYears() {
      const years = []
      this.hotList.concat(this.coldList).forEach((row) => {
        const year = row.recruit_year === undefined || row.recruit_year === null ? '' : String(row.recruit_year)
        if (year && years.indexOf(year) === -1) years.push(year)
      })
      if (years.length) this.recruitYears = years.sort()
    },
    handleModeChange() {
      this.query.keyword = ''
      this.getRankings()
    },
    resetFilters() {
      this.viewMode = 'major'
      this.query = DEFAULT_QUERY()
      this.getRankings()
    },
    formatInteger(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toLocaleString('zh-CN') : '--'
    },
    numberValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : '--'
    },
    formatRate(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(2) : '--'
    },
    enrolTypeName(value) {
      const item = this.enrolTypeOptions.find((option) => option.value === Number(value))
      return item ? item.label : (value || '--')
    },
    codedName(code, name) {
      if (!code && !name) return '--'
      return code ? `（${code}）${name || '--'}` : (name || '--')
    },
    entityTitle(row) {
      return this.viewMode === 'major'
        ? this.codedName(row.college_code, row.college_name)
        : this.codedName(row.major_code, row.major_name)
    },
    entityMeta(row) {
      const relatedName = this.viewMode === 'major'
        ? this.codedName(row.major_code, row.major_name)
        : this.codedName(row.college_code, row.college_name)
      return `${relatedName} · ${this.enrolTypeName(row.enrol_type)}`
    },
    recordKey(row) { return row.id || `${row.target_year}-${row.college_code}-${row.major_code}-${row.enrol_type}` },
    rowKey(row, index, type) { return `${type}-${this.recordKey(row)}-${index}` },
    rankLabel(index) { return String(index + 1).padStart(2, '0') },
  },
}
</script>

<style lang="less" scoped>
.target-recruit-page { min-height: 100%; }
.breadcrumb, .overview-card /deep/ .el-card__body, .ranking-summary, .ranking-header, .ranking-column-head, .ranking-list li { align-items: center; display: flex; }
.breadcrumb, .ranking-summary, .ranking-header, .ranking-column-head { justify-content: space-between; }
.breadcrumb { height: 30px; margin-bottom: 12px; }
.overview-card, .filter-card { margin-bottom: 14px; }
.overview-card /deep/ .el-card__body { padding: 16px 20px; }
.overview-statistic, .scope-statistic { box-sizing: border-box; min-width: 0; }
.overview-statistic { flex: 1; }
.scope-statistic { border-left: 1px solid #ebeef5; flex: 0 0 430px; padding-left: 28px; }
.overview-statistic > span, .scope-statistic > span { color: #909399; display: block; font-size: 13px; }
.overview-statistic strong { color: #409eff; display: inline-block; font-size: 27px; line-height: 34px; margin-right: 12px; }
.scope-statistic strong { color: #303133; display: block; font-size: 17px; line-height: 28px; }
.overview-statistic small, .scope-statistic small { color: #909399; font-size: 12px; }
.filter-card /deep/ .el-card__body { padding: 16px 18px 12px; }
.filter-form { display: flex; flex-wrap: wrap; gap: 0 8px; }
.filter-form /deep/ .el-form-item { margin-bottom: 10px; margin-right: 0; }
.keyword-input { width: 230px; }
.filter-form /deep/ .el-select { width: 128px; }
.recruit-year-input { width: 170px; }
.filter-actions { margin-left: auto; }
.filter-tip { color: #909399; font-size: 12px; line-height: 18px; }
.filter-tip i { color: #409eff; margin-right: 5px; }
.ranking-summary { color: #909399; font-size: 13px; margin: 2px 2px 10px; }
.ranking-summary strong { color: #303133; font-weight: 600; }
.ranking-grid { display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); min-height: 330px; }
.ranking-panel { background: #fff; border: 1px solid #e4e7ed; border-radius: 6px; box-sizing: border-box; min-width: 0; overflow: hidden; }
.ranking-header { min-height: 67px; padding: 0 18px; }
.ranking-header h2 { color: #303133; font-size: 17px; font-weight: 600; line-height: 24px; margin: 0 0 3px; }
.ranking-header h2 i { margin-right: 7px; }
.ranking-header p { color: #909399; font-size: 12px; margin: 0; }
.ranking-count { background: #f4f4f5; border-radius: 11px; color: #909399; font-size: 12px; line-height: 22px; padding: 0 9px; }
.hot-panel .ranking-header h2, .hot-panel .rate-value, .hot-panel .rank-number.top { color: #f06b21; }
.cold-panel .ranking-header h2, .cold-panel .rate-value, .cold-panel .rank-number.top { color: #18975d; }
.ranking-column-head { background: #f5f7fa; color: #909399; font-size: 12px; padding: 9px 18px; }
.ranking-list { list-style: none; margin: 0; padding: 0; }
.ranking-list li { border-bottom: 1px solid #ebeef5; box-sizing: border-box; gap: 11px; min-height: 78px; padding: 10px 18px; }
.rank-number { color: #909399; flex: 0 0 28px; font-size: 14px; font-variant-numeric: tabular-nums; font-weight: 600; }
.target-info { flex: 1; min-width: 0; }
.target-info > strong, .target-info > small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.target-info > strong { color: #303133; font-size: 14px; line-height: 21px; }
.target-info > small { color: #909399; font-size: 12px; line-height: 19px; }
.target-info p { color: #606266; font-size: 12px; line-height: 20px; margin: 1px 0 0; }
.target-info p b { color: #303133; font-weight: 600; }
.target-info p span { border-left: 1px solid #dcdfe6; margin: 0 9px; }
.rate-value { flex: 0 0 84px; font-size: 20px; font-variant-numeric: tabular-nums; font-weight: 600; text-align: right; }
.rate-value small { font-size: 13px; font-weight: 500; }
.ranking-empty { color: #909399; min-height: 310px; padding-top: 90px; text-align: center; }
.ranking-empty i { color: #c0c4cc; font-size: 42px; }
.ranking-empty p { margin: 10px 0; }
.ranking-panel footer { font-size: 12px; line-height: 20px; padding: 9px 18px; }
.hot-panel footer { background: #fff7ed; color: #a96532; }
.cold-panel footer { background: #f0f9f4; color: #5b826e; }
.overlap-alert { margin-top: 14px; }
@media screen and (max-width: 1280px) {
  .overview-card /deep/ .el-card__body { align-items: flex-start; }
  .scope-statistic { flex-basis: 360px; }
  .filter-actions { margin-left: 0; }
}
@media screen and (max-width: 1050px) {
  .ranking-grid { grid-template-columns: 1fr; }
}
@media screen and (max-width: 760px) {
  .overview-card /deep/ .el-card__body { display: block; }
  .scope-statistic { border-left: 0; border-top: 1px solid #ebeef5; margin-top: 12px; padding: 12px 0 0; }
  .filter-form { display: block; }
  .keyword-input, .filter-form /deep/ .el-select, .recruit-year-input { width: 100%; }
  .ranking-list li { padding-left: 12px; padding-right: 12px; }
}
</style>
